import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { completeRedirectSignIn, signIn } from '../../lib/firebase.ts';
import { IconBolt, IconGoogle, IconShield, IconSparkles, Mark } from '../ui/Icons.tsx';

const EASE = [0.22, 1, 0.36, 1] as const;

const POINTS = [
  { Icon: IconSparkles, text: 'Say what you want to achieve — it plans the whole campaign.' },
  { Icon: IconShield, text: 'Nothing spends money or publishes without your explicit approval.' },
  { Icon: IconBolt, text: 'Meta, Google Ads and YouTube payloads, built and previewed in full.' },
];

/** Firebase's codes are precise but unreadable. The domain one especially — it is a console
 *  setting, not anything the person signing in can fix, so say so instead of showing a code. */
function readableError(code: string, message: string): string {
  switch (code) {
    case 'auth/unauthorized-domain':
      return 'This domain is not authorised in Firebase yet. Add it under Authentication → Settings → Authorized domains.';
    case 'auth/popup-closed-by-user':
      return 'Sign-in was cancelled.';
    case 'auth/network-request-failed':
      return 'Network problem reaching Google. Check your connection and try again.';
    default:
      return message || 'Sign-in failed. Please try again.';
  }
}

export function SignIn() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A redirect sign-in finishes on page load, not in the click handler.
  useEffect(() => {
    void completeRedirectSignIn();
  }, []);

  async function onClick() {
    setError(null);
    setBusy(true);
    try {
      await signIn();
    } catch (err) {
      const e = err as { code?: string; message?: string };
      setError(readableError(e.code ?? '', e.message ?? ''));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="lx-auth">
      <div className="lx-aurora" />

      <motion.div
        className="lx-auth-card"
        initial={{ opacity: 0, y: 20, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.55, ease: EASE }}
      >
        <div className="lx-authmark">
          <Mark size={52} />
        </div>

        <h1>
          Welcome to <span className="lx-grad-text">Lumlix</span>
        </h1>
        <p>Describe the outcome. Your agent runs the marketing.</p>

        <motion.button
          className="lx-gbtn lx-focus"
          onClick={() => void onClick()}
          disabled={busy}
          whileTap={{ scale: 0.985 }}
        >
          <IconGoogle size={19} />
          {busy ? 'Opening Google…' : 'Continue with Google'}
        </motion.button>

        {error && (
          <motion.p
            className="lx-err-note"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {error}
          </motion.p>
        )}

        <div className="lx-auth-points">
          {POINTS.map(({ Icon, text }, i) => (
            <motion.div
              key={text}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: 0.25 + i * 0.09, ease: EASE }}
            >
              <Icon size={16} />
              <span>{text}</span>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}
